import siteConfig from '@/config';
import { useDemoMode } from '@/context/DemoModeContext';
import { storeDeepLinkIntent } from '@/lib/deeplink';
import PATH_PAGE from '@/router/path';
import { RouteParams } from '@/router/type';
import { useGetUser } from '@/service/user/hook';
import { ArrowRight, X, Megaphone } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useMatch, useNavigate, useParams } from 'react-router-dom';

type AnnouncementItem = {
  id: string;
  label: string;
  cta: string;
  action: (orgId: string, wsId: string, navigate: (path: string) => void) => void;
};

const ANNOUNCEMENTS: AnnouncementItem[] = [
  {
    id: 'one-click-deploy',
    label: 'New: Deploy vLLM models instantly with One-Click Deployment.',
    cta: 'Try it now',
    action: (orgId, wsId, navigate) => {
      storeDeepLinkIntent({ action: 'gpu-instance', type: 'one-click-deploy', timestamp: Date.now() });
      navigate(PATH_PAGE.dashboard.gpuInstance.create(orgId, wsId));
    },
  },
];

const DISMISSED_KEY = 'global_announcement_dismissed';

const GlobalAlertInActive = () => {
  const { isDemoMode } = useDemoMode();
  const { data: user, isLoading } = useGetUser({ staleTime: Infinity });
  const { orgId = '', workspaceId = '' } = useParams<RouteParams>();
  const navigate = useNavigate();
  const isLandingPage = useMatch('/dashboard/:orgId/:workspaceId');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Rotate announcement every 5 seconds
  useEffect(() => {
    if (dismissed || !user?.is_active) return;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [dismissed, user?.is_active]);

  if (isDemoMode || isLoading) return null;

  // Inactive account warning
  if (!user?.is_active) {
    return (
      <div className="text-warning-foreground flex justify-center gap-2 bg-warning p-1 px-6 text-center">
        <span className="font-bold">Warning:</span>
        <span>Your account is restricted and features are unavailable.</span>
        {siteConfig.services.setting.payment && (
          <Link
            to={PATH_PAGE.setting.payment}
            className="flex items-center gap-1 underline"
          >
            Go to payment settings for more details{' '}
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    );
  }

  // News / announcement banner — landing page only
  if (!isLandingPage || dismissed) return null;

  const item = ANNOUNCEMENTS[currentIndex];

  return (
    <div className="relative flex items-center justify-center gap-2 bg-band-blue px-10 py-1 text-center text-sm text-white">
      <Megaphone className="h-3.5 w-3.5 shrink-0 opacity-80" />
      <span className="truncate">{item.label}</span>
      {orgId && workspaceId && (
        <button
          onClick={() => item.action(orgId, workspaceId, navigate)}
          className="flex shrink-0 items-center gap-1 font-semibold underline underline-offset-2"
        >
          {item.cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
      {/* Dots */}
      <div className="flex items-center gap-1 ml-2">
        {ANNOUNCEMENTS.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`h-1.5 w-1.5 rounded-full transition-opacity ${i === currentIndex ? 'opacity-100 bg-white' : 'opacity-40 bg-white'}`}
          />
        ))}
      </div>
      <button
        onClick={() => {
          setDismissed(true);
          try { localStorage.setItem(DISMISSED_KEY, 'true'); } catch (e) { /* ignore */ }
        }}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-70 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default GlobalAlertInActive;
